// Deliverable logic: ownership, urgency, effort weighting, rendering one row.
//
// Two ownership shapes coexist deliberately:
//
//   kind "group"                  → lead + second (person ids, either may be empty)
//   kind "individual" | "exam"    → owners[] — everyone who has to do it
//
// Use ownersOf() rather than reading the fields, so callers do not have to care.

import { C, EFFORT, LEAD_W, SECOND_W, STATUS_LABEL } from "../data/classes.js";
import { P } from "../data/people.js";
import { d, daysBetween, fmtFull, today } from "./dates.js";
import { el, esc } from "./dom.js";
import { S } from "./state.js";

/** Everyone with a stake in this item, leads first. */
export function ownersOf(it) {
  if (it.kind === "group") return [it.lead, it.second].filter((x) => x && P[x]);
  return (it.owners || []).filter((x) => P[x]);
}

/** A team deliverable with no lead is the thing the dashboard shouts about. */
export function needsLead(it) {
  return it.kind === "group" && !(it.lead && P[it.lead]) && it.status !== "done";
}

/**
 * Effort split across owners, in EFFORT units.
 * Lead 70%, second 30%; a lead with no second carries the lot. Non-team work returns
 * {} — exams and individual projects cannot be delegated, so counting them would make
 * the load chart lie about what is actually shareable.
 */
export function loadShares(it) {
  if (it.kind !== "group") return {};
  const w = EFFORT[it.effort || "M"];
  const lead = it.lead && P[it.lead] ? it.lead : null;
  const second = it.second && P[it.second] ? it.second : null;
  if (lead && second) return { [lead]: w * LEAD_W, [second]: w * SECOND_W };
  if (lead) return { [lead]: w };
  if (second) return { [second]: w };
  return {};
}

export function urgency(it) {
  if (it.status === "done") return "done";
  const gap = daysBetween(today(), d(it.due));
  if (gap < 0) return "overdue";
  if (gap <= 2) return "soon";
  return "normal";
}

export function avatar(pid, role) {
  const p = P[pid];
  if (!p) return "";
  const cls = "av" + (role ? " " + role : "") + (pid === S.me ? " me" : "");
  const suffix = role === "lead" ? " — lead" : role === "second" ? " — second" : "";
  return `<span class="${cls}" title="${esc(p.name + suffix)}">${p.ini}</span>`;
}

export function classChip(cid) {
  const c = C[cid];
  if (!c) return "";
  return `<span class="clschip" style="background:var(${c.s});color:var(${c.v})" title="${esc(c.full)}">${cid}</span>`;
}

/** Short label for tight spaces — calendar chips. "15.515 Homework #1" → "515 HW #1". */
export function shortTitle(it) {
  const s = it.title
    .replace(/\s+[—-]\s.*$/, "")
    .replace(/^Homework\s*/, "HW ")
    .replace(/^Deliverable\s*/, "D")
    .replace(/^Group Case Memo/, "Memo")
    .replace(/^Team Project /, "Project ")
    .replace(/^Persuasive Presentation/, "Pres")
    .replace(/^Self-Assessment/, "Self-assess")
    .replace(/^Negotiation /, "Negot. ")
    .replace(/ with faculty advisor$/, "");
  return it.cls.replace("15.", "") + " " + s;
}

/**
 * One deliverable as a grid row.
 * @param {object} it
 * @param {{showDate?: boolean}} [opts] — false when the day is already the group heading
 */
export function itemRow(it, opts = {}) {
  const c = C[it.cls] || { v: "--line-strong", s: "--surface-2" };
  const u = urgency(it);
  const row = el("div", "item" + (it.status === "done" ? " isdone" : "") + (u === "overdue" ? " overdue" : ""));

  let side;
  if (it.kind === "group") {
    side =
      '<span class="roles">' +
      (it.lead && P[it.lead] ? `<span class="role"><span class="rl lead">Lead</span>${avatar(it.lead, "lead")}</span>` : "") +
      (it.second && P[it.second] ? `<span class="role"><span class="rl">2nd</span>${avatar(it.second, "second")}</span>` : "") +
      "</span>";
  } else {
    side = '<span class="owners">' + ownersOf(it).map((x) => avatar(x)).join("") + "</span>";
  }

  let dueLabel = opts.showDate === false ? "" : fmtFull(it.due);
  if (it.dueTime) dueLabel += (dueLabel ? " · " : "") + it.dueTime;
  if (it.window) dueLabel = it.window;

  const kindLabel = it.kind === "group" ? "Team" : it.kind === "exam" ? "Exam" : "Individual";

  row.innerHTML =
    `<div class="stripe" style="background:var(${c.v})"></div>` +
    classChip(it.cls) +
    `<div class="itmain">
       <div class="ttl">${esc(it.title)}</div>
       <div class="meta">
         <span class="kindpill ${it.kind}">${kindLabel}</span>
         ${dueLabel ? `<span class="duetime${u === "soon" || u === "overdue" ? " due-soon" : ""}">${esc(dueLabel)}${u === "overdue" ? " · overdue" : ""}</span>` : ""}
         ${it.kind === "group" ? `<span class="eff">${it.effort || "M"}</span>` : ""}
       </div>
       ${it.note ? `<div class="note">${esc(it.note)}</div>` : ""}
     </div>
     <div class="itside">
       ${needsLead(it) ? '<span class="unassigned">needs a lead</span>' : side}
       <button class="statusbtn ${it.status}" data-act="status" data-id="${esc(it.id)}">${STATUS_LABEL[it.status]}</button>
       <button class="rowbtn" data-act="edit" data-id="${esc(it.id)}">Edit</button>
     </div>`;
  return row;
}
